/**
 * ProTableListForm — 独立受控「一条数据对多数组/树形 + 多级表头(扩展)」可编辑表格组件。
 *
 * 对外只暴露 value + onChange（受控），可被 Form.Item 当普通字段消费。
 * 核心引擎不 import 业务模型；业务通过 subArrays / columns / rules 注入。
 * ⚠️ 零共享、不依赖 TableListForm：本文件为 ProTableListForm 独立定义的全套泛型。
 */

// ============================================================
// 基础行/子项约束
// ============================================================
/** 主行最小约束（需有稳定 id） */
export interface RowBase {
  id: RowId;
  [key: string]: unknown;
}
/** 子项最小约束（需有稳定 id） */
export interface ChildBase {
  id: ChildId;
  [key: string]: unknown;
}
export type RowId = string;
export type ChildId = string;
export type ColumnFixed = 'left' | 'right';
/**
 * 子数组「子列动作」的落点列（见 `SubArraySpec.rowActionColumn`）。
 *
 * - `'second'`（默认）：第二子列 cell 底部；子列不足两个时回退第一子列。
 * - `'first'`：第一子列 cell 底部 —— 与「+添加{emptyLabel}」同 cell、同行并排。
 */
export type RowActionColumn = 'first' | 'second';

// ============================================================
// 字段控件类型
// ============================================================
/** 子项格控件类型（对应 SubCellContent 的渲染） */
export type SubFieldType = 'select' | 'input' | 'visible' | 'checkbox' | 'custom';
/** 正常列控件类型（对应 CellField 的渲染） */
export type DocFieldType = 'input' | 'select';
/** 下拉选项 */
export interface FieldOption {
  name: string;
  value: string;
}

/**
 * 【列头固定按钮】固定在列头角落的操作按钮（`left` / `right` 与 flex 对齐直觉一致）。
 *
 * 与 `title` / `batchEdit` / `titleExtra` 等**互不耦合**：这里只声明「要固定在角落的内容」，
 * 定位、层级、不遮挡 title 点击等**全部由引擎内部承担**（见 `ColumnFixedButtonLayer`）。
 *
 * 定位落点为列头的 **padding 外沿**（即忽略列头自身 padding 的物理角落），
 * 且**不受 title 宽度 / 换行 / 行数影响**。
 */
export interface ColumnFixedButton {
  /** 固定于列头左上角 */
  left?: React.ReactNode;
  /** 固定于列头右上角 */
  right?: React.ReactNode;
}

/** 子列 meta：描述子数组里一个子项字段长什么样（同时是子项格内控件宽度基准） */
export interface SubColumnMeta<TChild> {
  key: string;
  field: string;
  title: string;
  width: number;
  /**
   * 【列宽可拖拽】可选：表头右边缘出现拖拽把手，用户可自行调整列宽
   * （对齐设计稿「全量字段展示样式（默认最小宽度），要支持用户自己拖拽列宽」）。
   * ⚠️ 底层 react-resizable 要求宽度是**数字**，故仅在 `width` 为数字时才可拖。
   */
  resizable?: boolean;
  type: SubFieldType;
  options?: FieldOption[];
  required?: boolean;
  /**
   * type==='custom' 时：自定义子列渲染（item + patch 回调 + 兄弟子项）。
   * `items` 为该行该子数组的全部子项（含自身），供需要「行内上下文」的控件使用
   * （如 kpi_code 下拉按本行已选指标禁用重复项，对齐面板 KPITable 的 data.some(...)）。
   */
  render?: (item: TChild, patch: (p: Record<string, unknown>) => void, items: TChild[]) => React.ReactNode;
  /**
   * 【格式校验】可选：值级校验，返回错误信息（无错返回 undefined）。
   *
   * 与 required 叠加：required 判空、validator 判格式（如「非负 + ≤4 位小数」）。
   * 引擎的实时标红（SubCellContent）与整体校验（findFirstDoc）消费同一份规则，
   * 避免 UI 提示与提交校验两份逻辑漂移。
   *
   * `item` 为当前子项（引擎传入，类型为 unknown）：格式规则常依赖子项上下文
   * （如按 `kpi_schema.type` 分派数值/文本校验），业务方按需断言。
   * ⚠️ 签名与 `FieldRule.validator` 保持一致，以便直接作为引擎校验规则消费。
   */
  validator?: (value: unknown, item?: unknown) => string | undefined;
  /**
   * 【可编辑性】可选：判定该子项此字段当前是否渲染为**受控（可编辑）控件**（默认 true）。
   *
   * **校验只覆盖可编辑字段**：只读展示 / disabled 控件用户改不了，对其报错会造成
   * 「提交被拦却无从修复」的死锁。故实时标红（SubCellContent）与整体校验（findFirstDoc）
   * 都消费本判据。
   *
   * ⚠️ 判据是「控件是否受控」，**不是**「行数据状态」：同一行中个别字段只读、其余仍可编辑时，
   * 只有只读的那些字段退出校验，可编辑字段照常校验。业务方应复用各 render 分支的**同一个**
   * 只读判定（如 `conf.view || disabled`），避免「渲染成只读、校验仍必填」两处漂移。
   */
  editable?: (item: TChild) => boolean;
}

/** 正常列 meta：描述主行一个普通字段长什么样 */
export interface DocFieldMeta<TRow> {
  key: string;
  field: string;
  title: string;
  width: number;
  /** 【列宽可拖拽】可选，语义同 SubColumnMeta.resizable */
  resizable?: boolean;
  type: DocFieldType;
  options?: FieldOption[];
  required?: boolean;
  fixed?: ColumnFixed;
  /** 列头固定按钮（左上/右上角），与 title 布局解耦，见 ColumnFixedButton */
  fixedButton?: ColumnFixedButton;
}

/** 自定义列 meta：业务完全掌控 render（如行操作：复制/删除） */
export interface CustomColumnMeta<TRow> {
  key: string;
  title: string;
  width: number;
  /** 【列宽可拖拽】可选，语义同 SubColumnMeta.resizable */
  resizable?: boolean;
  fixed?: ColumnFixed;
  render: (row: TRow) => React.ReactNode;
  /**
   * 【批量编辑】可选：表头「批量编辑」popover 的元数据。提供后该列标题渲染为
   * BatchEditCellTitle（保留/覆盖 + 填值控件），填值控件由 batchEdit.render 自定义
   *（业务传控件，引擎只取值消费）；未提供则标题为纯文本（无批量编辑入口）。
   */
  batchEdit?: BatchEditMeta;
  /**
   * 【钉在左侧】可选：列标题旁挂载的额外 ReactNode（如「钉住」Pushpin 按钮）。
   * 有 batchEdit 时渲染在 BatchEditCellTitle 标题旁，无 batchEdit 时与标题一并展示。
   *
   * ⚠️ 它随 title **参与布局流**（会撑高/挪动标题区）。若诉求是「固定在列头角落、
   * 不受 title 布局影响」，请改用 `fixedButton`。
   */
  titleExtra?: React.ReactNode;
  /**
   * 【列头固定按钮】固定在列头左上/右上角，与 title 布局、列头 padding 解耦。
   * 是「钉在左侧」等角落操作入口的推荐做法（引擎内部封装定位，见 ColumnFixedButtonLayer）。
   */
  fixedButton?: ColumnFixedButton;
  /**
   * 【校验】可选：自定义列的错误判定，返回错误信息（无错返回 undefined）。
   *
   * custom 列的值常嵌套存放（如属性列的 `row.property_values[code]`），引擎无从取值，
   * 故由业务方在此提供判据。引擎整体校验（findFirstDoc / validateAndLocate / validateFields）
   * 按列的实际展示顺序遍历时调用它，使 custom 列同样参与校验并滚动定位。
   * ⚠️ 建议与列内红框判定复用同一函数，避免 UI 与校验两份逻辑漂移。
   */
  validate?: (row: TRow) => string | undefined;
}

/** 子数组描述符（抽象核心）：声明主行哪个字段是子数组、如何读/写/增 + 子列 */
export interface SubArraySpec<TRow, TChild> {
  /** 逻辑 key（列 id 前缀/校验定位） */
  key: string;
  /** 组标题（表头红星加在此） */
  title: string;
  /**
   * 【整体必填】可选：组标题渲染红星 + 引擎空子项校验（该行子数组为空时判错并
   * 可滚动定位到「+添加{emptyLabel}」按钮所在 cell）。仅控制「有没有子项」，
   * 子项内部字段的必填仍由 SubColumnMeta.required 控制。
   * ⚠️ 需与折叠面板模式的整体必填判定保持同一来源（如 KPI 的 cooperateKpiRequired）。
   */
  required?: boolean;
  /** 读取子数组 */
  getItems: (row: TRow) => TChild[];
  /** 不可变写回子数组 */
  setItems: (row: TRow, items: TChild[]) => TRow;
  /** 新增子项默认工厂 */
  makeDefault: () => TChild;
  /** 子列（该子数组的字段，每子列独立 cell） */
  columns: SubColumnMeta<TChild>[];
  /** "添加{emptyLabel}" 文案，如 "指标"/"权益" */
  emptyLabel: string;
  /** 子项 id 前缀（默认取 item.id） */
  idPrefix?: string;
  getItemId?: (item: TChild) => ChildId;
  /** 是否启用删除操作子列（默认 true）；false 时不渲染该子数组的「操作」列 */
  showDelete?: boolean;
  /**
   * 是否隐藏「+添加{emptyLabel}」（默认 false）。对齐面板 KPITable 的 disableAdd。
   *
   * ⚠️ 本字段**同时决定该子数组是否占用「添加行」高度**：引擎的渲染（renderSubColCell 的 showAdd）
   * 与行高计算（sideHeight / rowSignature）同源消费它，两处不会漂移。
   * 故凡是无添加入口的场景（查看态、拆单「已有执行周期」），都应传 true —— 否则按钮虽不渲染，
   * 行底仍会残留一行（40px）空白。
   */
  disableAdd?: boolean;
  /**
   * 【行级组可见性】可选：判定某主行的该子数组是否参与渲染，默认全部可见。
   *
   * 返回 false 时该行该子数组**整组消失**：所有子列 cell（含拖拽子列与操作子列）渲染为空、
   * 不计入行高、且**跳过校验**。对齐折叠面板的行级整块隐藏，如 KPI 的
   * `need_cooperate_account_supplier_settle_price && 该行 need_actual_execution === false`
   * → 该条合作内容的整个 KPI 面板消失。
   *
   * ⚠️ 本字段**只影响渲染，不触碰数据**。引擎的写路径（patchItem / addItem / removeItem /
   * requestAddItem）一律以 `getItems` 为基底，故**不可**用「在 getItems 里过滤掉这些子项」
   * 来替代本字段 —— 那会在该行任意一次编辑时把被过滤的子项静默写没（数据需要保留，
   * 判定条件可能再切回来）。
   *
   * ⚠️ 校验必须同步跳过：否则 required 的组会在不可见行上报错，用户提交被拦却
   * 定位到一个看不见的 cell（见 findFirstDoc）。
   */
  visibleRow?: (row: TRow) => boolean;
  /** 是否禁用全部子项删除（默认 false）。对齐面板 KPITable 的 disableDelete */
  disableDelete?: boolean;
  /**
   * 子项最少保留条数（默认 0）：子项数 <= minItems 时删除按钮置灰，避免把子数组删空。
   * 与 disableDelete 叠加（任一命中即置灰）。对齐面板 KPITable 的 ableDelete
   * （必填指标至少留 1 条）→ 传 `minItems: required ? 1 : 0`。
   */
  minItems?: number;
  /**
   * 自定义"添加子项"请求（可选）：若提供，"+添加{emptyLabel}"点击时引擎调用它而非默认
   * push(makeDefault())——用于需要弹窗选择而非直接插入空行的场景（如关联权益库）。
   * 引擎把 (row, commit) 传给业务方；业务方异步流程（如弹窗 onOk）完成后调用 commit(items)，
   * 引擎负责把 items 追加进该行该子数组并 onChange 写回（不可变）。
   */
  onAddRequest?: (row: TRow, commit: (items: TChild[]) => void) => void;
  /**
   * 是否为该子数组的子项启用拖拽重排（默认 false）。开启时引擎会在子列最前插入一个
   * 拖拽手柄操作列，拖拽释放后按新顺序不可变写回该行该子数组。
   */
  sortable?: boolean;
  /**
   * 拖拽跟随预览文案（可选，配合 sortable）：拖拽子项时鼠标跟随的「整行信息快照」
   * 由该文案渲染（如 `权益库-标准权益：${item.content}`），解决默认 drag image
   * 只有把手图标、无法辨认正在拖哪条的问题。不提供时回退默认行为（仅图标跟随）。
   */
  getDragLabel?: (item: TChild) => string;
  /**
   * 【子列动作】可选：在子列 cell 底部渲染一个「按行取源」的动作节点（如「应用至整单」）。
   *
   * 【为什么落在子列里，而不是表格下方工具条】子数组级动作的源是**某一行**的子数组
   * （「把这一行的指标覆盖到整单」）—— 只有在行内才能表达「以哪一行为源」；
   * 工具条没有行上下文，只能再加一个选行的下拉，交互更重。
   *
   * 【落点】默认渲染在该子数组的**第二子列**（`columns[1]`）cell 底部；子列不足两个时回退
   * 第一子列（`columns[0]`）。可用 `rowActionColumn: 'first'` 改为落在**第一子列** ——
   * 此时与「+添加{emptyLabel}」在**同一 cell、同一行**并排（见 `renderSubColCell` 的合并渲染）。
   *
   * 【⚠️ 行高】与添加入口**共用同一个高度槽位**，且同样受 `subHasAddRow(sub)` 门控
   * （`disableAdd` 为真时**一并不渲染**）—— 故不影响 `sideHeight` / `computeRowSignature`。
   * 见 `rowMetrics` 的「唯一判据源」约定：**不要**让本动作独立占行，
   * 那会让「实际 DOM 高度」比引擎算出的行高多 40px（表现为行底被裁切 / 错位）。
   *
   * 【校验】本动作纯展示，不参与 `findFirstDoc`（不是输入控件，没有可校验的值）。
   */
  renderRowAction?: (row: TRow) => React.ReactNode;
  /**
   * 【子列动作的落点列】可选，默认 `'second'`（见 `RowActionColumn`）。
   *
   * ⚠️ 本字段**只决定动作落在哪一格**，不影响行高：两种落点都与添加入口共用同一个末尾行
   * （见 `renderRowAction` 的行高说明），故 `sideHeight` / `computeRowSignature` 无需感知它。
   *
   * ⚠️ 落 `'first'` 时动作与添加按钮**共用同一容器**：该容器的红框判据是 `showAdd && !!addError`
   * （见 `renderSubColCell` 的 `showAddError`），故「整体必填却无子项」不会把动作一并染红。
   */
  rowActionColumn?: RowActionColumn;
}

/** 列 meta 总（业务注入：正常列 + 子数组组列 + 自定义列） */
export type ColumnMeta<TRow, TChild> =
  | { kind: 'doc'; doc: DocFieldMeta<TRow> }
  | { kind: 'subArray'; sub: SubArraySpec<TRow, TChild> }
  | { kind: 'custom'; custom: CustomColumnMeta<TRow> };

// ============================================================
// 校验
// ============================================================
/**
 * 值类型（对齐 `@bedrock/field-form` 的 `RuleType`）。
 * 用于 `FieldRule.type`：值类型判定 + `len/min/max` 的度量口径。
 */
export type FieldRuleType =
  | 'string' | 'number' | 'boolean' | 'method' | 'regexp' | 'integer'
  | 'float' | 'object' | 'array' | 'enum' | 'date' | 'url' | 'hex' | 'email';

/**
 * Form.Item 形态的校验器（对齐 `@bedrock/field-form` 的 `Validator`）：
 * `(rule, value, callback) => void | Promise`。报错通过 `callback('信息')`，
 * 无错则不调用 callback。
 *
 * ⚠️ 引擎的校验是**同步**的（实时标红在渲染中判定），故**不支持返回 rejected Promise 的异步报错**；
 * 异步校验器请改用同步形态的 `FieldRuleValidatorSync`。
 */
export type FieldRuleValidatorFn = (
  rule: FieldRule,
  value: unknown,
  callback: (error?: string) => void,
) => void;

/**
 * 引擎的简易同步校验器：直接返回错误信息（无错返回 undefined）。
 * `item` 为所属子项（主列校验时为 undefined），便于按子项上下文做格式校验。
 */
export type FieldRuleValidatorSync = (value: unknown, item?: unknown) => string | undefined;

/**
 * 字段校验规则 —— **与 `@bedrock/field-form` 的 `Rule` 结构对齐**，Form.Item 的 rules 写法可直接搬用。
 *
 * 类型来源：`@bedrock/field-form/es/interface.d.ts:23-52`（`BaseRule` + `ValidatorRule`）。
 * 该包未从根导出 `Rule`，且项目约定不硬依赖其私有类型（见 `test-bridge/types.ts`），
 * 故按结构复刻于此 —— **上游改动时需对照同步**。
 *
 * 已支持（同步语义由 `validateValue` 实现，逐条对齐 async-validator）：
 * `required` / `pattern` / `message` / `enum` / `len` / `min` / `max` / `whitespace` / `transform` / `type`。
 *
 * **明确不支持**（有意不声明，写了会直接 TS 报错 —— 比「声明了却静默失效」更安全）：
 *  - `defaultField`：仅在 `type: 'array'` 时给数组每个元素套规则；引擎的数组值极少
 *    （子数组是独立的列模型，不走这里），无实际用途。
 *  - `validateTrigger`：引擎的校验触发时机是**格级**的（`touched` + `forceValidate` 统一控制），
 *    **没有 per-rule 触发概念**，要做需把判定细化到规则级，收益低。
 *  - `warningOnly`（仅警告不阻断）：**未实现，且已刻意移除该字段**。引擎当前只有「错误」一种态，
 *    保留占位会让业务写出「以为只警告、实际被拦截」的静默失效规则。真要做需先由产品定义
 *    警告的交互（红框还是黄框、是否算校验失败、能否提交），再引入 `level` 概念。
 */
export interface FieldRule {
  required?: boolean;
  pattern?: RegExp;
  message?: string;
  /** 枚举白名单：值须命中其一（`type: 'enum'` 时配合使用） */
  enum?: unknown[];
  /** 长度（字符串/数组）/ 数值（number 类型为相等值）—— 对齐 async-validator 口径 */
  len?: number;
  min?: number;
  max?: number;
  /** 纯空白字符串视为错误（仅对 string 生效） */
  whitespace?: boolean;
  /** 校验前对值做转换（转换结果同时用于后续规则判定） */
  transform?: (value: unknown) => unknown;
  /** 值类型判定；`len/min/max` 的度量口径随它分派 */
  type?: FieldRuleType;
  /**
   * 校验器，支持两种写法：
   *  - **Form.Item 形态**（3 参）：`(rule, value, callback) => void` —— 与 `Rule.validator` 一致；
   *  - **引擎简易形态**（≤2 参）：`(value, item) => string | undefined` —— 直接返回错误信息。
   * 引擎按**形参个数**区分（`fn.length >= 3` 视为 Form.Item 形态）。
   */
  validator?: FieldRuleValidatorFn | FieldRuleValidatorSync;
}

/**
 * 动态规则（对齐 `@bedrock/field-form` 的 `RuleRender`）：按表单实例生成规则。
 *
 * 引擎在收集规则时调用它并**展开**结果 —— 用于「依赖表单其他字段动态决定本字段规则」的场景。
 * 参数为引擎内部的表单实例（类型收敛为 unknown，业务按需断言）。
 */
export type FieldRuleRender = (form: unknown) => FieldRule;

/** 规则数组的元素：静态规则或动态规则 */
export type FieldRuleItem = FieldRule | FieldRuleRender;

/** 字段 → 规则集（引擎据此做校验标红 / 滚动定位） */
export type FieldRules = Record<string, FieldRuleItem[]>;

/** 判定是否为动态规则（函数形态） */
export const isRuleRender = (r: FieldRuleItem): r is FieldRuleRender => typeof r === 'function';

/** 校验问题定位信息 */
export interface ValidationIssue<TRow = RowBase> {
  rowId: RowId;
  /** 组 key：'document' 或某 subArray key */
  group: string;
  childId?: ChildId;
  field: string;
  message: string;
}

/** 校验器（可选注入；缺省时引擎按 subArrays + rules 自动实现） */
export interface TableValidator<TRow, TChild> {
  rules: FieldRules;
  validateField: (row: TRow, field: string, childId?: ChildId) => ValidationIssue<TRow>[];
  validateAll: (rows: TRow[]) => ValidationIssue<TRow>[];
  findFirst: (rows: TRow[]) => ValidationIssue<TRow> | undefined;
}

// ============================================================
// 批量编辑 meta（主列 batch edit 用）
// ============================================================
export interface BatchEditMeta {
  type: DocFieldType;
  options?: FieldOption[];
  required?: boolean;
  /**
   * 【批量编辑】可选：自定义填值控件。提供时 BatchEditCellTitle 用它渲染填值区
   * （引擎只消费 onChange 的值 → onBatchApply），不提供则回退内置 input/select。
   * 用于批量弹层控件与列内单元格控件保持一致（下拉/时间/数字/多选等）。
   */
  render?: (value: any, onChange: (v: any) => void) => React.ReactNode;
  /**
   * 【批量编辑】可选：自定义写回调。custom 列批量时引擎不直接写 `r[field]`（属性列是
   * 嵌套 property_values），业务方用 apply 自行写回；不提供则回退引擎 handleBatchApply（标量）。
   */
  apply?: (value: any, mode: 'keep' | 'cover') => void;
}

// ============================================================
// 组件 Props / Handle —— **唯一定义源**
// ============================================================
/**
 * 组件 Props。
 *
 * ⚠️ 这里**刻意删掉了 6 个历史字段** —— 它们曾是「传了不生效」的静默陷阱：
 *  - types.ts 侧：`validator` / `estimateRowHeight`
 *  - 组件侧：`validateAll` / `findFirst` / `rowKey`
 *  - 两侧都有且组件内解构后**从未使用**：`enableMultiLevelHeader`
 *
 * 删掉后若仍传这些字段会**编译报错**（这是期望行为）。自定义校验只能走
 * `ColumnMeta.custom.validate` / `SubColumnMeta.validator` / `FieldRule`。
 */
export interface ProTableListFormProps<TRow extends RowBase, TChild extends ChildBase> {
  /** 受控主行数组（整棵复杂结构，含各子数组） */
  value: TRow[];
  onChange: (next: TRow[]) => void;
  /** 子数组声明 */
  subArrays: SubArraySpec<TRow, TChild>[];
  /** 列声明（正常列 + 子数组组列） */
  columns: ColumnMeta<TRow, TChild>[];
  /** 字段校验规则（引擎据此标红/定位） */
  rules?: FieldRules;
  /** 容器高度：直接作为 scroll.y（虚拟滚动视窗高）。与 maxHeight 二选一 */
  height?: number;
  /**
   * 容器封顶高度（可选）：传值时引擎算 scroll.y = min(maxHeight, 内容总高)，
   * 使容器随内容/子项自适应展开、封顶后滚动。height 优先（显式固定值）。
   */
  maxHeight?: number;
  /** 容器最小高度（可选）：传值时引擎算 scroll.y = max(minHeight, min(maxHeight, 内容总高))，
   * 避免单行/无子项时容器过扁（容纳表头+内容行+添加子项行）。 */
  minHeight?: number;
}

export interface ProTableListFormHandle {
  /**
   * ⚠️ **恒返回 `false` 的假 API**：类型声明是 `(coord) => boolean`，实现是 `() => false`
   * （忽略入参）。保留声明仅为兼容既有调用点；**新代码请用 `validateAndLocate()` /
   * `validateFields()`**，不要依赖本方法的返回值。
   */
  locateToError: (coord: { rowId: RowId; field: string; childId?: ChildId }) => boolean;
  validateAndLocate: () => boolean;
  validateFields: () => Promise<void>;
  batchApply: (field: string, value: string, mode: 'keep' | 'cover') => void;
  /**
   * ⚠️ **空实现**（`() => {}`）：外部调用静默无反应，不新增行、不触发 `onChange`。
   * 业务需自持 addRow，并保证每行有 `id`（草稿行无 id，渲染前补、回写前剥）。
   */
  addRow: () => void;
  removeRow: (id: RowId) => void;
  copyRow: (id: RowId) => void;
  /**
   * 把最后一行滚入可视区（表格纵向滚到底）。
   *
   * 【用途】表格高度按内容自适应但受 `maxHeight` 封顶，封顶后「新增一行」会落在
   * 视口外或只露出半行。业务在新增行后调用本方法，让新行完整可见，无需用户再手动滚动。
   */
  scrollToLastRow: () => void;
}
